import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, FileBarChart, ShieldCheck, LogOut, FlaskConical, Globe,
} from "lucide-react";

export function LangSwitcher() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-xs font-medium" data-testid="language-switcher">
      <button
        data-testid="lang-en-btn"
        onClick={() => setLang("en")}
        className={`px-2.5 py-1 rounded transition-colors duration-200 ${lang === "en" ? "bg-graphite text-white" : "text-slate-500 hover:text-graphite"}`}
      >
        English
      </button>
      <button
        data-testid="lang-te-btn"
        onClick={() => setLang("te")}
        className={`px-2.5 py-1 rounded transition-colors duration-200 ${lang === "te" ? "bg-graphite text-white" : "text-slate-500 hover:text-graphite"}`}
      >
        తెలుగు
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, demoMode, logout, exitDemo, scope, viewScope, setViewScope } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav_dashboard"), testid: "nav-dashboard" },
    { to: "/families", icon: Users, label: t("nav_families"), testid: "nav-families" },
    { to: "/reports", icon: FileBarChart, label: t("nav_reports"), testid: "nav-reports" },
  ];
  if (!demoMode && user?.role === "admin") {
    navItems.push({ to: "/admin/users", icon: ShieldCheck, label: t("nav_users"), testid: "nav-users" });
  }

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-60 shrink-0 bg-graphite flex flex-col fixed inset-y-0 z-30 no-print">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src="/assets/logo.png" alt="Vaskion Gram Survey" className="h-10 w-10 rounded bg-white object-contain p-0.5" />
          <div>
            <div className="text-white font-heading font-semibold text-sm leading-tight">Vaskion</div>
            <div className="text-white/60 text-[11px] leading-tight">Gram Survey</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive ? "bg-white/10 text-white font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10 text-[10px] text-white/40">
          {t("company")}
        </div>
      </aside>

      <div className="flex-1 ml-60 flex flex-col min-h-screen">
        {scope === "demo" && (
          <div
            data-testid="demo-banner"
            className="bg-amber-100 text-amber-800 text-xs font-medium text-center py-1.5 px-4 flex items-center justify-center gap-2"
          >
            <FlaskConical className="h-3.5 w-3.5" />
            {t("demo_banner")}
          </div>
        )}
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-20 no-print">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-graphite">K. Gopalapuram</span>
            {scope === "demo" && (
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" data-testid="demo-mode-badge">
                {t("demo_mode")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Globe className="h-4 w-4 text-slate-400" />
            <LangSwitcher />
            {user && !demoMode && (
              <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-xs font-medium" data-testid="scope-toggle">
                <button
                  data-testid="scope-demo-btn"
                  onClick={() => setViewScope("demo")}
                  className={`px-2.5 py-1 rounded transition-colors duration-200 ${viewScope === "demo" ? "bg-amber-500 text-white" : "text-slate-500 hover:text-graphite"}`}
                >
                  {t("scope_demo_data")}
                </button>
                <button
                  data-testid="scope-production-btn"
                  onClick={() => setViewScope("production")}
                  className={`px-2.5 py-1 rounded transition-colors duration-200 ${viewScope === "production" ? "bg-graphite text-white" : "text-slate-500 hover:text-graphite"}`}
                >
                  {t("scope_production")}
                </button>
              </div>
            )}
            {demoMode ? (
              <Button
                data-testid="exit-demo-btn"
                variant="outline"
                size="sm"
                onClick={() => {
                  exitDemo();
                  navigate("/");
                }}
              >
                {t("exit_demo")}
              </Button>
            ) : (
              user && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-graphite leading-tight" data-testid="header-user-name">{user.name}</div>
                    <div className="text-[11px] text-slate-500 capitalize">{t(`role_${user.role}`)}</div>
                  </div>
                  <Button
                    data-testid="logout-btn"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                  >
                    <LogOut className="h-3.5 w-3.5 mr-1.5" />
                    {t("nav_logout")}
                  </Button>
                </div>
              )
            )}
          </div>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
